import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  addManagementAPI,
  updateManagementAPI,
  getManagementsListAPI,
  getManagementsAPI,
  getManagementDetailAPI,
  importManagementsCsvAPI,
  deleteManagementAPI,
  resendHrInviteAPI,
} from "./managmentApi";

export const addManagement = createAsyncThunk(
  "managements/add",
  async ({ data }, { rejectWithValue }) => {  // ✅ Only expect data
    try {
      const response = await addManagementAPI({ data });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to add management");
    }
  }
);

// ✅ Update
export const updateManagement = createAsyncThunk(
  "managements/update",
  async ({ company_employee_id , data }, { rejectWithValue }) => {
    try {
      const response = await updateManagementAPI({ company_employee_id, data });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to update management");
    }
  }
);

// ✅ Delete
export const deleteManagement = createAsyncThunk(
  "managements/delete",
  async ({ company_employee_id, company_id }, { rejectWithValue }) => {
    try {
      const response = await deleteManagementAPI({ company_employee_id, company_id });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to delete management");
    }
  }
);

// ✅ Resend HR Invite
export const resendHrInvite = createAsyncThunk(
  "managements/resendHrInvite",
  async ({ companyId }, { rejectWithValue }) => {
    try {
      const response = await resendHrInviteAPI({ companyId });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to resend HR invite");
    }
  }
);

// ✅ List (with pagination)
export const getManagementsList = createAsyncThunk(
  "managements/list",
  async (params, { rejectWithValue }) => {
    try {
      const { company_id, page = 1, limit = 10, search = "", sort, order } = params || {};
      const response = await getManagementsListAPI({ company_id, page, limit, search, sort, order });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch managements");
    }
  }
);

// ✅ Get All Managements (simple get)
export const getManagements = createAsyncThunk(
  "managements/getAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getManagementsAPI();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch managements");
    }
  }
);

// ✅ Detail
export const getManagementDetail = createAsyncThunk(
  "managements/detail",
  async ({ companyId, managementId }, { rejectWithValue }) => {
    try {
      const response = await getManagementDetailAPI({ companyId, managementId });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch management detail");
    }
  }
);

// ✅ Import CSV
export const importManagementsCsv = createAsyncThunk(
  "managements/importCsv",
  async ({ companyId, formData }, { rejectWithValue }) => {
    try {
      const response = await importManagementsCsvAPI({ companyId, formData });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to import managements");
    }
  }
);

const initialState = {
  addManagement: { data: null, loading: false, error: null },
  updateManagement: { data: null, loading: false, error: null },
  deleteManagement: { data: null, loading: false, error: null },
  resendHrInvite: { data: null, loading: false, error: null },
  getManagementsList: { data: [], total: 0, loading: false, error: null },
  getManagements: { data: [], total: 0, loading: false, error: null },
  getManagementDetail: { data: null, loading: false, error: null },
  importManagementsCsv: { data: null, loading: false, error: null },
};

const managementsSlice = createSlice({
  name: "managements",
  initialState,
  reducers: {
    clearAddManagement: (state) => {
      state.addManagement.data = null;
      state.addManagement.error = null;
    },
    clearUpdateManagement: (state) => {
      state.updateManagement.data = null;
      state.updateManagement.error = null;
    },
    clearDeleteManagement: (state) => {
      state.deleteManagement.data = null;
      state.deleteManagement.error = null;
    },
    clearResendHrInvite: (state) => {
      state.resendHrInvite.data = null;
      state.resendHrInvite.error = null;
    },
    clearManagementDetail: (state) => {
      state.getManagementDetail.data = null;
      state.getManagementDetail.error = null;
    },
    clearImportManagementsCsv: (state) => {
      state.importManagementsCsv.data = null;
      state.importManagementsCsv.error = null;
    },
    clearGetManagements: (state) => {
      state.getManagements.data = [];
      state.getManagements.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Add
      .addCase(addManagement.pending, (state) => {
        state.addManagement.loading = true;
        state.addManagement.error = null;
      })
      .addCase(addManagement.fulfilled, (state, action) => {
        state.addManagement.loading = false;
        state.addManagement.data = action.payload;
      })
      .addCase(addManagement.rejected, (state, action) => {
        state.addManagement.loading = false;
        state.addManagement.error = action.payload;
      })

      // Update
      .addCase(updateManagement.pending, (state) => {
        state.updateManagement.loading = true;
        state.updateManagement.error = null;
      })
      .addCase(updateManagement.fulfilled, (state, action) => {
        state.updateManagement.loading = false;
        state.updateManagement.data = action.payload;
      })
      .addCase(updateManagement.rejected, (state, action) => {
        state.updateManagement.loading = false;
        state.updateManagement.error = action.payload;
      })

      // Delete
      .addCase(deleteManagement.pending, (state) => {
        state.deleteManagement.loading = true;
        state.deleteManagement.error = null;
      })
      .addCase(deleteManagement.fulfilled, (state, action) => {
        state.deleteManagement.loading = false;
        state.deleteManagement.data = action.payload;
      })
      .addCase(deleteManagement.rejected, (state, action) => {
        state.deleteManagement.loading = false;
        state.deleteManagement.error = action.payload;
      })

      // Resend HR Invite
      .addCase(resendHrInvite.pending, (state) => {
        state.resendHrInvite.loading = true;
        state.resendHrInvite.error = null;
      })
      .addCase(resendHrInvite.fulfilled, (state, action) => {
        state.resendHrInvite.loading = false;
        state.resendHrInvite.data = action.payload;
      })
      .addCase(resendHrInvite.rejected, (state, action) => {
        state.resendHrInvite.loading = false;
        state.resendHrInvite.error = action.payload;
      })

      // List (with pagination)
      .addCase(getManagementsList.pending, (state) => {
        state.getManagementsList.loading = true;
        state.getManagementsList.error = null;
      })
      .addCase(getManagementsList.fulfilled, (state, action) => {
        state.getManagementsList.loading = false;
        state.getManagementsList.data = action.payload?.data || [];
        state.getManagementsList.total =
          action.payload?.pagination?.total ||
          action.payload?.count ||
          (Array.isArray(action.payload?.data) ? action.payload.data.length : 0);
      })
      .addCase(getManagementsList.rejected, (state, action) => {
        state.getManagementsList.loading = false;
        state.getManagementsList.error = action.payload;
      })

      // Get All Managements (without pagination)
      .addCase(getManagements.pending, (state) => {
        state.getManagements.loading = true;
        state.getManagements.error = null;
      })
      .addCase(getManagements.fulfilled, (state, action) => {
        state.getManagements.loading = false;
        state.getManagements.data = action.payload?.data || [];
        state.getManagements.total =
          action.payload?.total ||
          action.payload?.count ||
          (Array.isArray(action.payload?.data) ? action.payload.data.length : 0);
      })
      .addCase(getManagements.rejected, (state, action) => {
        state.getManagements.loading = false;
        state.getManagements.error = action.payload;
      })

      // Detail
      .addCase(getManagementDetail.pending, (state) => {
        state.getManagementDetail.loading = true;
        state.getManagementDetail.error = null;
      })
      .addCase(getManagementDetail.fulfilled, (state, action) => {
        state.getManagementDetail.loading = false;
        state.getManagementDetail.data = action.payload?.data || action.payload;
      })
      .addCase(getManagementDetail.rejected, (state, action) => {
        state.getManagementDetail.loading = false;
        state.getManagementDetail.error = action.payload;
      })

      // Import CSV
      .addCase(importManagementsCsv.pending, (state) => {
        state.importManagementsCsv.loading = true;
        state.importManagementsCsv.error = null;
      })
      .addCase(importManagementsCsv.fulfilled, (state, action) => {
        state.importManagementsCsv.loading = false;
        state.importManagementsCsv.data = action.payload;
      })
      .addCase(importManagementsCsv.rejected, (state, action) => {
        state.importManagementsCsv.loading = false;
        state.importManagementsCsv.error = action.payload;
      });
  },
});

export const {
  clearAddManagement,
  clearUpdateManagement,
  clearDeleteManagement,
  clearResendHrInvite,
  clearManagementDetail,
  clearImportManagementsCsv,
  clearGetManagements,
} = managementsSlice.actions;

export default managementsSlice.reducer;